import { useEffect, useLayoutEffect, useState } from "react";

import type { RefObject } from "react";

export type Chapter = "paper" | "surface" | "navy";

// matchMedia가 없거나 값을 돌려주지 않는 환경(테스트, 일부 웹뷰)에서도 터지지 않게 한다.
const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);

/**
 * 헤더 잉크: 화면 맨 위(헤더 가운데 선)에 걸린 장의 바탕색을 따라 헤더가 바뀐다.
 * data-chapter가 붙은 구역 중 그 선과 겹친 마지막 구역이 기준이다.
 */
export const useHeaderChapter = (
  root: RefObject<HTMLElement | null>,
): Chapter => {
  const [chapter, setChapter] = useState<Chapter>("paper");

  useEffect(() => {
    const host = root.current;
    if (!host || typeof IntersectionObserver === "undefined") {
      return;
    }
    const sections = Array.from(
      host.querySelectorAll<HTMLElement>("[data-chapter]"),
    );
    const hits = new Set<HTMLElement>();
    let observer: IntersectionObserver | null = null;

    const settle = () => {
      const current = sections.filter((section) => hits.has(section)).pop();
      if (current) {
        setChapter(current.dataset.chapter as Chapter);
      }
    };
    const connect = () => {
      observer?.disconnect();
      hits.clear();
      // 위에서 34px~38px 사이의 얇은 띠만 본다(헤더 높이의 가운데).
      const below = Math.max(window.innerHeight - 38, 0);
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              hits.add(entry.target as HTMLElement);
            } else {
              hits.delete(entry.target as HTMLElement);
            }
          }
          settle();
        },
        { rootMargin: `-34px 0px -${below}px 0px` },
      );
      sections.forEach((section) => observer!.observe(section));
    };
    connect();
    window.addEventListener("resize", connect);
    return () => {
      window.removeEventListener("resize", connect);
      observer?.disconnect();
    };
  }, [root]);

  return chapter;
};

/**
 * 구역 진입: [data-rise] 요소가 화면에 들어오면 한 번만 is-in을 붙인다.
 * 같은 순간에 들어온 요소끼리는 80ms 간격으로 차례로 오른다(제목 → 본문 → 그림).
 * 숨김은 JS가 붙인 cl-js 클래스 아래에서만 시작하므로, 스크립트가 실패해도 보인다.
 */
export const useRiseOnEnter = (root: RefObject<HTMLElement | null>) => {
  useLayoutEffect(() => {
    const host = root.current;
    if (!host) {
      return;
    }
    const targets = Array.from(
      host.querySelectorAll<HTMLElement>("[data-rise]"),
    );
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      targets.forEach((target) => target.classList.add("is-in"));
      return;
    }
    host.classList.add("cl-js");
    let frame = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        // 이미 지나쳐 올라간 요소(새로고침 뒤 스크롤 복원 등)는 바로 보인다.
        entries
          .filter(
            (entry) =>
              !entry.isIntersecting && entry.boundingClientRect.bottom < 0,
          )
          .forEach((entry) => {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          });
        const entering = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) =>
            a.target.compareDocumentPosition(b.target) &
            Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1,
          );
        entering.forEach((entry, index) => {
          const target = entry.target as HTMLElement;
          target.style.setProperty("--cl-delay", `${index * 80}ms`);
          target.classList.add("is-in");
          observer.unobserve(target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    // 숨김 상태를 한 프레임 그린 뒤 관찰을 시작해야 첫 화면도 올라오며 나타난다.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        targets.forEach((target) => observer.observe(target));
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      host.classList.remove("cl-js");
    };
  }, [root]);
};

/** 히어로 영상 틀: 스크롤에 따라 아주 약하게 커진다(약 4%). transform만 쓴다. */
export const useScrollScale = (frame: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const element = frame.current;
    if (!element || prefersReducedMotion()) {
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const box = element.getBoundingClientRect();
      const span = window.innerHeight + box.height;
      const progress = Math.min(
        Math.max((window.innerHeight - box.top) / span, 0),
        1,
      );
      element.style.setProperty(
        "--cl-scale",
        (0.96 + progress * 0.04).toFixed(4),
      );
    };
    const onScroll = () => {
      if (!raf) {
        raf = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [frame]);
};
